// https://github.com/tldraw/tldraw/blob/5db3c1553e14edd14aa5f7c0fc85fc5efc352335/packages/store/src/lib/StoreQueries.ts#L0-L1
// Fixed some imports
import { computed, EMPTY_ARRAY, isUninitialized, RESET_VALUE, withDiff } from "@supersig/state";
import { isEqual } from "es-toolkit";
// import {
//   executeQuery,
//   objectMatchesQuery,
//   QueryExpression,
// } from "./executeQuery";
import { objectMapValues } from "@supersig/utils";
import { objectMatchesQuery } from "./executeQuery.mjs";
import { IncrementalSetConstructor } from "./IncrementalSetConstructor.mjs";
import { diffSets, intersectSets } from "./setUtils.mjs";
/**
 * A class that provides a 'namespace' for the various kinds of indexes one may wish to derive from
 * the record store.
 */ export class StoreQueries {
    atoms;
    history;
    constructor(atoms, history){
        this.atoms = atoms;
        this.history = history;
    }
    /**
   * A cache of derivations (indexes).
   *
   * @internal
   */ indexCache = new Map();
    /**
   * A cache of type-based indexes to avoid full scans during large operations.
   * Maps typeName to Set of record IDs of that type.
   */ typeIndexCache = new Map();
    /**
   * Canonical unfiltered queries by record type.
   * These are hot in the editor and should not allocate fresh reactive graphs.
   */ allIdsCache = new Map();
    allRecordsCache = new Map();
    allRecordCache = new Map();
    /**
   * A cache of derivations (filtered histories).
   *
   * @internal
   */ historyCache = new Map();
    getAllIdsQuery(typeName) {
        let cached = this.allIdsCache.get(typeName);
        if (cached) {
            return cached;
        }
        const typeHistory = this.filterHistory(typeName);
        const typeIndex = this.filterIndex(typeName);
        cached = computed("ids:" + typeName, (prevValue, lastComputedEpoch)=>{
            const fromScratch = ()=>{
                typeHistory.get();
                return typeIndex.get();
            };
            const buildDiff = (added, removed)=>{
                if (!added.size && !removed.size) {
                    return null;
                }
                return {
                    ...added.size ? {
                        added
                    } : {},
                    ...removed.size ? {
                        removed
                    } : {}
                };
            };
            if (isUninitialized(prevValue)) {
                return fromScratch();
            }
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                const nextValue = fromScratch();
                const diff = diffSets(prevValue, nextValue);
                return diff ? withDiff(nextValue, diff) : prevValue;
            }
            const nextValue = new Set(prevValue);
            const added = new Set();
            const removed = new Set();
            const add = (id)=>{
                if (nextValue.has(id)) {
                    return;
                }
                nextValue.add(id);
                if (!removed.delete(id)) {
                    added.add(id);
                }
            };
            const remove = (id)=>{
                if (!nextValue.delete(id)) {
                    removed.delete(id);
                    return;
                }
                if (!added.delete(id)) {
                    removed.add(id);
                }
            };
            for (const changes of history){
                for (const record of objectMapValues(changes.added)){
                    add(record.id);
                }
                for (const record of objectMapValues(changes.removed)){
                    remove(record.id);
                }
            }
            const diff = buildDiff(added, removed);
            return diff ? withDiff(nextValue, diff) : prevValue;
        }, {
            historyLength: 50
        });
        this.allIdsCache.set(typeName, cached);
        return cached;
    }
    getAllRecordsQuery(typeName) {
        let cached = this.allRecordsCache.get(typeName);
        if (cached) {
            return cached;
        }
        const ids = this.getAllIdsQuery(typeName);
        cached = this.createRecordsQuery(typeName, "records:" + typeName, ids);
        this.allRecordsCache.set(typeName, cached);
        return cached;
    }
    getAllRecordQuery(typeName) {
        let cached = this.allRecordCache.get(typeName);
        if (cached) {
            return cached;
        }
        const ids = this.getAllIdsQuery(typeName);
        cached = computed("record:" + typeName, ()=>{
            for (const id of ids.get()){
                return this.atoms.get()[id]?.get();
            }
            return undefined;
        });
        this.allRecordCache.set(typeName, cached);
        return cached;
    }
    /**
   * Create a derivation that contains the hisotry for a given type
   *
   * @param typeName - The name of the type to filter by.
   * @returns A derivation that returns the ids of all records of the given type.
   * @public
   */ filterHistory(typeName) {
        if (this.historyCache.has(typeName)) {
            return this.historyCache.get(typeName);
        }
        const filtered = computed("filterHistory:" + typeName, (lastValue, lastComputedEpoch)=>{
            if (isUninitialized(lastValue)) {
                return this.history.get();
            }
            const diff = this.history.getDiffSince(lastComputedEpoch);
            if (diff === RESET_VALUE) return this.history.get();
            const res = {
                added: {},
                removed: {},
                updated: {}
            };
            let numAdded = 0;
            let numRemoved = 0;
            let numUpdated = 0;
            for (const changes of diff){
                for (const added of objectMapValues(changes.added)){
                    if (added.typeName === typeName) {
                        if (res.removed[added.id]) {
                            const original = res.removed[added.id];
                            delete res.removed[added.id];
                            numRemoved--;
                            if (original !== added) {
                                res.updated[added.id] = [
                                    original,
                                    added
                                ];
                                numUpdated++;
                            }
                        } else {
                            res.added[added.id] = added;
                            numAdded++;
                        }
                    }
                }
                for (const [from, to] of objectMapValues(changes.updated)){
                    if (to.typeName === typeName) {
                        if (res.added[to.id]) {
                            res.added[to.id] = to;
                        } else if (res.updated[to.id]) {
                            res.updated[to.id] = [
                                res.updated[to.id][0],
                                to
                            ];
                        } else {
                            res.updated[to.id] = [
                                from,
                                to
                            ];
                            numUpdated++;
                        }
                    }
                }
                for (const removed of objectMapValues(changes.removed)){
                    if (removed.typeName === typeName) {
                        if (res.added[removed.id]) {
                            // was added during this diff sequence, so just undo the add
                            delete res.added[removed.id];
                            numAdded--;
                        } else if (res.updated[removed.id]) {
                            // remove oldest version
                            res.removed[removed.id] = res.updated[removed.id][0];
                            delete res.updated[removed.id];
                            numUpdated--;
                            numRemoved++;
                        } else {
                            res.removed[removed.id] = removed;
                            numRemoved++;
                        }
                    }
                }
            }
            if (numAdded || numRemoved || numUpdated) {
                return withDiff(this.history.get(), res);
            } else {
                return lastValue;
            }
        }, {
            historyLength: 100
        });
        this.historyCache.set(typeName, filtered);
        return filtered;
    }
    /**
   * Get or create a type-based index for efficient type filtering.
   * This avoids the need to scan all atoms when filtering by type.
   * Especially important for large stores as it avoids the need to scan all atoms and THEN filtering by type.
   */ filterIndex(typeName) {
        let typeIndex = this.typeIndexCache.get(typeName);
        if (!typeIndex) {
            const typeHistory = this.filterHistory(typeName);
            typeIndex = computed(`typeIndex:${typeName}`, (prevValue, lastComputedEpoch)=>{
                if (isUninitialized(prevValue)) {
                    // initial computation scanning all atoms once (this is what we used to always do)
                    typeHistory.get(); // Subscribe to history
                    const result = new Set();
                    for (const atom of objectMapValues(this.atoms.get())){
                        const record = atom.get();
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    return result;
                }
                // incremental update using history diffs
                const history = typeHistory.getDiffSince(lastComputedEpoch);
                if (history === RESET_VALUE) {
                    // fall back to full atom scan if needed
                    const result = new Set();
                    for (const atom of objectMapValues(this.atoms.get())){
                        const record = atom.get();
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    return result;
                }
                // apply incremental changes - history is an array of diffs
                const result = new Set(prevValue);
                for (const diff of history){
                    // add new records of this type
                    for (const record of objectMapValues(diff.added)){
                        if (record.typeName === typeName) {
                            result.add(record.id);
                        }
                    }
                    // handle updated records (type shouldn't change, but include for completeness)
                    for (const [from, to] of objectMapValues(diff.updated)){
                        if (to.typeName === typeName) {
                            result.add(to.id);
                        }
                    }
                    // remove deleted records
                    for (const record of objectMapValues(diff.removed)){
                        if (record.typeName === typeName) {
                            result.delete(record.id);
                        }
                    }
                }
                return result;
            }, {
                historyLength: 50
            });
            this.typeIndexCache.set(typeName, typeIndex);
        }
        return typeIndex;
    }
    /**
   * Create a derivation that returns an index on a property for the given type.
   *
   * @param typeName - The name of the type.
   * @param property - The name of the property.
   * @public
   */ index(typeName, property) {
        const cacheKey = typeName + ":" + property;
        if (this.indexCache.has(cacheKey)) {
            return this.indexCache.get(cacheKey);
        }
        const index = this.__uncached_createIndex(typeName, property);
        this.indexCache.set(cacheKey, index);
        return index;
    }
    /**
   * Create a derivation that returns an index on a property for the given type.
   *
   * @param typeName - The name of the type?.
   * @param property - The name of the property?.
   * @internal
   */ __uncached_createIndex(typeName, property) {
        const typeHistory = this.filterHistory(typeName);
        const fromScratch = ()=>{
            // deref typeHistory early so that the first time the incremental version runs
            // it gets a diff to work with instead of having to bail to this from-scratch version
            typeHistory.get();
            const res = new Map();
            for (const atom of objectMapValues(this.atoms.get())){
                const record = atom.get();
                if (record.typeName === typeName) {
                    const value = record[property];
                    if (!res.has(value)) {
                        res.set(value, new Set());
                    }
                    res.get(value).add(record.id);
                }
            }
            return res;
        };
        return computed("index:" + typeName + ":" + property, (prevValue, lastComputedEpoch)=>{
            if (isUninitialized(prevValue)) return fromScratch();
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                return fromScratch();
            }
            const setConstructors = new Map();
            const add = (value, id)=>{
                let setConstructor = setConstructors.get(value);
                if (!setConstructor) setConstructor = new IncrementalSetConstructor(prevValue.get(value) ?? new Set());
                setConstructor.add(id);
                setConstructors.set(value, setConstructor);
            };
            const remove = (value, id)=>{
                let set = setConstructors.get(value);
                if (!set) set = new IncrementalSetConstructor(prevValue.get(value) ?? new Set());
                set.remove(id);
                setConstructors.set(value, set);
            };
            for (const changes of history){
                for (const record of objectMapValues(changes.added)){
                    if (record.typeName === typeName) {
                        const value = record[property];
                        add(value, record.id);
                    }
                }
                for (const [from, to] of objectMapValues(changes.updated)){
                    if (to.typeName === typeName) {
                        const prev = from[property];
                        const next = to[property];
                        if (prev !== next) {
                            remove(prev, to.id);
                            add(next, to.id);
                        }
                    }
                }
                for (const record of objectMapValues(changes.removed)){
                    if (record.typeName === typeName) {
                        const value = record[property];
                        remove(value, record.id);
                    }
                }
            }
            let nextValue = undefined;
            let nextDiff = undefined;
            for (const [value, setConstructor] of setConstructors){
                const result = setConstructor.get();
                if (!result) continue;
                if (!nextValue) nextValue = new Map(prevValue);
                if (!nextDiff) nextDiff = new Map();
                if (result.value.size === 0) {
                    nextValue.delete(value);
                } else {
                    nextValue.set(value, result.value);
                }
                nextDiff.set(value, result.diff);
            }
            if (nextValue && nextDiff) {
                return withDiff(nextValue, nextDiff);
            }
            return prevValue;
        }, {
            historyLength: 100
        });
    }
    /**
   * Create a derivation that will return a signle record matching the given query.
   *
   * It will return undefined if there is no matching record
   *
   * @param typeName - The name of the type?
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ record(typeName, queryCreator, name = "record:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        if (!queryCreator) {
            return this.getAllRecordQuery(typeName);
        }
        const ids = this.ids(typeName, queryCreator, name);
        return computed(name, ()=>{
            for (const id of ids.get()){
                return this.atoms.get()[id]?.get();
            }
            return undefined;
        });
    }
    /**
   * Create a derivation that will return an array of records matching the given query
   *
   * @param typeName - The name of the type?
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ records(typeName, queryCreator, name = "records:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        if (!queryCreator) {
            return this.getAllRecordsQuery(typeName);
        }
        const ids = this.ids(typeName, queryCreator, "ids:" + name);
        return this.createRecordsQuery(typeName, name, ids);
    }
    /**
   * Create a derivation that will return the ids of all records of the given type.
   *
   * @param typeName - The name of the type.
   * @param queryCreator - A function that returns the query expression.
   * @param name - (optinal) The name of the query.
   */ ids(typeName, queryCreator, name = "ids:" + typeName + (queryCreator ? ":" + queryCreator.toString() : "")) {
        if (!queryCreator) {
            return this.getAllIdsQuery(typeName);
        }
        const typeHistory = this.filterHistory(typeName);
        const fromScratch = ()=>{
            // deref type history early to allow first incremental update to use diffs
            typeHistory.get();
            const query = queryCreator();
            if (Object.keys(query).length === 0) {
                return this.filterIndex(typeName).get();
            }
            return executeQuery(this, typeName, query);
        };
        const fromScratchWithDiff = (prevValue)=>{
            const nextValue = fromScratch();
            const diff = diffSets(prevValue, nextValue);
            if (diff) {
                return withDiff(nextValue, diff);
            } else {
                return prevValue;
            }
        };
        const cachedQuery = computed("ids_query:" + name, queryCreator, {
            isEqual
        });
        return computed("query:" + name, (prevValue, lastComputedEpoch)=>{
            const query = cachedQuery.get();
            if (isUninitialized(prevValue)) {
                return fromScratch();
            }
            // if the query changed since last time this ran then we need to start again
            if (lastComputedEpoch < cachedQuery.lastChangedEpoch) {
                return fromScratchWithDiff(prevValue);
            }
            // otherwise iterate over the changes from the store and apply them to the previous value if needed
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                return fromScratchWithDiff(prevValue);
            }
            const setConstructor = new IncrementalSetConstructor(prevValue);
            for (const changes of history){
                for (const added of objectMapValues(changes.added)){
                    if (added.typeName === typeName && objectMatchesQuery(query, added)) {
                        setConstructor.add(added.id);
                    }
                }
                for (const [_, updated] of objectMapValues(changes.updated)){
                    if (updated.typeName === typeName) {
                        if (objectMatchesQuery(query, updated)) {
                            setConstructor.add(updated.id);
                        } else {
                            setConstructor.remove(updated.id);
                        }
                    }
                }
                for (const removed of objectMapValues(changes.removed)){
                    if (removed.typeName === typeName) {
                        setConstructor.remove(removed.id);
                    }
                }
            }
            const result = setConstructor.get();
            if (!result) {
                return prevValue;
            }
            return withDiff(result.value, result.diff);
        }, {
            historyLength: 50
        });
    }
    exec(typeName, query) {
        const ids = executeQuery(this, typeName, query);
        if (ids.size === 0) {
            return EMPTY_ARRAY;
        }
        const atoms = this.atoms.get();
        return [
            ...ids
        ].map((id)=>atoms[id].get());
    }
    createRecordsQuery(typeName, name, ids) {
        const typeHistory = this.filterHistory(typeName);
        const fromScratch = ()=>{
            // Keep the query reactive to type membership and updates without
            // subscribing to every record atom individually.
            typeHistory.get();
            const nextIds = ids.get();
            const atoms = this.atoms.__unsafe__getWithoutCapture();
            const records = [];
            for (const id of nextIds){
                const atom = atoms[id];
                if (!atom) {
                    throw new Error("no atom found for record id: " + id);
                }
                records.push(atom.__unsafe__getWithoutCapture());
            }
            return records;
        };
        return computed(name, (prevValue, lastComputedEpoch)=>{
            if (isUninitialized(prevValue)) {
                return fromScratch();
            }
            const idsDiff = ids.getDiffSince(lastComputedEpoch);
            if (idsDiff === RESET_VALUE) {
                return fromScratch();
            }
            for (const diff of idsDiff){
                if (diff.added?.size || diff.removed?.size) {
                    return fromScratch();
                }
            }
            const history = typeHistory.getDiffSince(lastComputedEpoch);
            if (history === RESET_VALUE) {
                return fromScratch();
            }
            if (history.length === 0) {
                return prevValue;
            }
            const currentIds = ids.get();
            const updatedIds = new Set();
            for (const changes of history){
                for (const [_, to] of objectMapValues(changes.updated)){
                    const id = to.id;
                    if (currentIds.has(id)) {
                        updatedIds.add(id);
                    }
                }
            }
            if (updatedIds.size === 0) {
                return prevValue;
            }
            const atoms = this.atoms.__unsafe__getWithoutCapture();
            let nextValue;
            let indexById;
            const ensureIndex = ()=>{
                if (indexById) {
                    return indexById;
                }
                indexById = new Map();
                for(let i = 0, n = prevValue.length; i < n; i++){
                    indexById.set(prevValue[i].id, i);
                }
                return indexById;
            };
            for (const id of updatedIds){
                const atom = atoms[id];
                if (!atom) {
                    return fromScratch();
                }
                const index = ensureIndex().get(id);
                if (index === undefined) {
                    return fromScratch();
                }
                if (!nextValue) {
                    nextValue = [
                        ...prevValue
                    ];
                }
                nextValue[index] = atom.__unsafe__getWithoutCapture();
            }
            return nextValue ?? prevValue;
        });
    }
}
export function executeQuery(store, typeName, query) {
    const matchIds = Object.fromEntries(Object.keys(query).map((key)=>[
            key,
            new Set()
        ]));
    for (const [k, matcher] of Object.entries(query)){
        if ("eq" in matcher) {
            const index = store.index(typeName, k);
            const ids = index.get().get(matcher.eq);
            if (ids) {
                for (const id of ids){
                    matchIds[k].add(id);
                }
            }
        } else if ("neq" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (value !== matcher.neq) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("gt" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (value > matcher.gt) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("stw" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (typeof value === "string" && value.startsWith(matcher.stw)) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("in" in matcher) {
            // Test membership against a Set instead of Array.includes: O(n + m)
            // rather than O(n × m) for an `in` on a high-cardinality key (like
            // `id`). Iteration stays in index order so result ordering — which
            // callers like resource-variant selection observe — is unchanged.
            const wanted = new Set(matcher.in);
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if (wanted.has(value)) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("inStw" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                if ((typeof value === "string" || typeof value === "number") && matcher.inStw.some((s)=>value.toString().startsWith(s))) {
                    for (const id of ids){
                        matchIds[k].add(id);
                    }
                }
            }
        } else if ("hasProperty" in matcher) {
            const index = store.index(typeName, k);
            for (const [value, ids] of index.get()){
                for (const id of ids){
                    if (typeof value === "object" && value !== null && matcher.hasProperty in value) {
                        // make sure it isn't tombstoned
                        if (value[matcher.hasProperty] !== undefined && "type" in value[matcher.hasProperty] && value[matcher.hasProperty].type !== "tombstone") {
                            matchIds[k].add(id);
                        }
                    }
                }
            }
        }
    }
    return intersectSets(Object.values(matchIds));
}
